import { Button, IconButton, Typography } from 'procyon-ui'
import React, { useCallback, useState } from 'react'
import { enqueueNotification } from 'Utils/Helpers'
import { openURLExternally } from 'Utils/PureHelperFuctions'
import StepContent from './StepContent'
import { faCopy } from '@fortawesome/free-solid-svg-icons'

export default function AppCard({
  type,
  app,
  appList,
  activeOS,
  setActiveOS,
  onCopy,
  config = {},
  showDownloadButton
}) {
  const [showConfig, setShowConfig] = useState(false)
  const [step, setStep] = useState(0)
  const [selectedOS, setSelectedOS] = useState('rhel')
  const [selectedLinuxArch, setSelectedLinuxArch] = useState('debian_amd')
  const [showInstructionModal, setShowInstructionModal] = useState(false)
  const DisplayConfig = type === 'ServerAgent' || type === 'WindowAgent' || type === 'LinuxAgent'
  const stepLabels = ['Setup Config', 'Setup Agent']
  const total = 2
  const canPrev = step > 0
  const isLast = step === total - 1

  const cx = (...arr) => arr.filter(Boolean).join(' ')
  const prev = useCallback(() => setStep((s) => Math.max(0, s - 1)), [])
  const next = useCallback(() => {
    setStep((s) => Math.min(total - 1, s + 1))
  }, [step])

  const handleFinish = () => {
    setShowConfig(!showConfig)
  }
  const copyConfig = () => {
    // format config
    const textConfig = Object.entries(config)
      .map(([key, value]) => `${key}=${value}`)
      .join('\n')

    navigator.clipboard
      .writeText(textConfig)
      .then(() => enqueueNotification('Config copied to clipboard', 'info'))
      .catch(() => enqueueNotification('Failed to copy config', 'error'))
  }

  return (
    <div className='bg-[var(--color-common-bg-base)] border border-[var(--color-common-border)] rounded-md p-6 mb-6 w-full md:w-[48%]'>
      {/* Header */}
      <div className='flex items-center justify-center flex-col'>
        <div className='flex items-center gap-2 mb-2'>
          <img src='img/brand-logo/ping-logo-horizontal-color.svg' style={{ width: '120px' }} />
        </div>

        {/* Title & Subtitle */}
        <Typography variant='h3'>{app.name}</Typography>
        <Typography
          variant='body-regular'
          className='!text-sm text-[color:var(--color-font-secondary)] mb-3'
        >
          {app.osRequirement}
        </Typography>

        {/* Download button + copy */}
        <div className='flex items-center gap-2'>
          {showDownloadButton && (
            <Button
              variant='primary'
              onClick={() => openURLExternally(app.link)}
              disabled={!app.link}
              aria-label={`Download for ${app.osName}`}
            >
              Download for {app.osName}
            </Button>
          )}

          <Button
            variant='gray'
            onClick={onCopy}
            disabled={!app.link}
            className='p-2 rounded-md border border-[var(--color-common-border)] hover:bg-[var(--color-common-bg-secondary)] transition disabled:opacity-50'
            aria-label='Copy download link'
            title='Copy download link'
          >
            {/* FileCopy icon substitute */}
            {!showDownloadButton && 'Agent Link'}

            <svg
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 24 24'
              fill='currentColor'
              className='h-5 w-5'
            >
              <path d='M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z' />
            </svg>
          </Button>

          {DisplayConfig && (
            <Button
              variant='grayBlue'
              onClick={() => setShowConfig((s) => !s)}
              className='px-3 py-2 rounded-lg border border-[var(--color-common-border)] text-[var(--color-font-primary)] hover:bg-[var(--color-common-bg-secondary)] transition'
            >
              {showConfig ? 'Hide Instruction' : 'Show Instruction'}
            </Button>
          )}
        </div>
        {DisplayConfig && showConfig && (
          <div className='fixed inset-0 z-[1000] flex items-center justify-center'>
            <div
              className='absolute inset-0 bg-black/50 backdrop-blur-[1px]'
              onClick={() => setShowConfig(!showConfig)}
            />
            <div className='relative z-[1001] w-[min(96vw,720px)] rounded-[5px] max-h-[100vh] overflow-hidden bg-[var(--color-common-bg-base)] shadow-2xl ring-1 ring-black/5'>
              <div className='flex items-start justify-between border-b border-gray-100 px-6 py-4'>
                <div>
                  <Typography variant='h3'>Setup Instruction</Typography>
                </div>
                <Button variant='gray' onClick={() => setShowConfig(!showConfig)}>
                  Close
                </Button>
              </div>
              {/* Breadcrumb Stepper */}
              <div className='flex flex-wrap items-center gap-2 border-b border-gray-100 px-6 py-3'>
                {stepLabels.map((label, i) => (
                  <div key={label}>
                    <button
                      type='button'
                      onClick={() => {
                        setStep(i)
                      }}
                      className={[
                        'text-sm transition-colors',
                        i === step
                          ? 'text-[var(--color-font-primary)] font-semibold'
                          : i < step
                            ? 'text-gray-700 hover:text-gray-900'
                            : 'text-gray-400 hover:text-gray-600'
                      ].join(' ')}
                    >
                      {label}
                    </button>
                    {i < stepLabels.length - 1 && <span className='mx-1 text-gray-300'>›</span>}
                  </div>
                ))}
              </div>
              <div className='px-6 py-5 space-y-5'>
                <StepContent
                  type={type}
                  step={step}
                  selectedOS={selectedOS}
                  setSelectedOS={setSelectedOS}
                  selectedLinuxArch={selectedLinuxArch}
                  setSelectedLinuxArch={setSelectedLinuxArch}
                  config={config}
                  showInstructionModal={showInstructionModal}
                  setShowInstructionModal={setShowInstructionModal}
                />
              </div>
              <div className='flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4'>
                <Button variant='gray' onClick={prev} disabled={!step}>
                  Previous
                </Button>

                <div className='flex gap-4'>

                  <Button variant='primary' onClick={isLast ? handleFinish : next}>
                    {isLast ? 'Finish' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* OS Selector */}
      <div className='mt-4 pt-3 border-t border-[var(--color-common-border)]'>
        <div className={`grid ${appList.length === 3 ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mt-2`}>
          {appList.map((item) => (
            <button
              key={item.osName}
              onClick={() => setActiveOS(item.osName)}
              className={cx(
                'flex items-center justify-center gap-2 rounded-lg border px-2 py-1.5 text-sm transition',
                'border-[var(--color-common-border)] hover:bg-[var(--color-common-bg-secondary)]',
                activeOS === item.osName && 'bg-[var(--color-common-bg-secondary)]'
              )}
            >
              {item.Icon && <item.Icon className='h-4 w-3' />}
              <span className='text-[var(--color-font-primary)] text-[14px]'>{item.osName}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
